import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames/bind';
import Icon from '../../elementals/icon';
import Text from '../../elementals/text';
import SortableList from '../sortableList';
import styles from './styles.scss';
const cx = classnames.bind(styles);
const itemArrayPropType = PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.node,
    value: PropTypes.string,
}).isRequired);
const renderSelectedListItem = (onRemove, option) => option && (_jsxs("div", { "data-id": option.value, "data-elm-id": 'item', className: cx('item'), style: { justifyContent: 'space-between' }, children: [option.text, _jsx(Icon, { "data-elm-id": 'remove_item', name: 'arrow-right', iconStyle: 'solid', color: 'grayMedium', hoverColor: 'error', className: cx('icon-button'), onClick: () => onRemove(option.value) })] }, option.value));
const renderUnselectedListItem = (onAdd, option) => option && (_jsxs("div", { "data-id": option.value, "data-elm-id": 'item', className: cx('item'), children: [_jsx(Icon, { "data-elm-id": 'add_item', name: 'arrow-left', iconStyle: 'solid', style: { marginRight: 12 }, color: 'grayMedium', hoverColor: 'success', className: cx('icon-button'), onClick: () => onAdd(option.value) }), option.text] }, option.value));
class Accumulator extends Component {
    static propTypes = {
        elmId: PropTypes.string,
        allOptions: itemArrayPropType,
        initialSelectedValues: PropTypes.arrayOf(PropTypes.string),
        onUpdate: PropTypes.func.isRequired,
        selectedListHeader: PropTypes.string,
        selectedListStyle: PropTypes.object,
        selectedListItemRenderer: PropTypes.func,
        unselectedListHeader: PropTypes.string,
        unselectedListItemRenderer: PropTypes.func,
        unselectedListStyle: PropTypes.object,
        unselectedSortKey: PropTypes.object,
        helpText: PropTypes.string,
        height: PropTypes.number,
        showAsGrid: PropTypes.bool,
        autoScrollDown: PropTypes.bool,
        allowUpdate: PropTypes.func,
        hideRightPanel: PropTypes.bool,
        triggerUpdate: PropTypes.number,
    };
    static defaultProps = {
        initialSelectedValues: [],
        selectedListHeader: 'Selected',
        selectedListStyle: null,
        selectedListItemRenderer: renderSelectedListItem,
        unselectedListHeader: 'Unselected',
        unselectedListStyle: null,
        unselectedListItemRenderer: renderUnselectedListItem,
        unselectedSortKey: { key: 'text', direction: 'asc' },
        helpText: '',
        height: 350,
        showAsGrid: false,
        autoScrollDown: false,
        allowUpdate: () => true,
        hideRightPanel: false,
        triggerUpdate: 0,
    };
    sortItems = items => {
        const { unselectedSortKey } = this.props;
        const directionVal = unselectedSortKey.direction === 'desc' ? -1 : 1;
        return items.sort((a, b) => {
            if (!!unselectedSortKey.key &&
                a[unselectedSortKey.key] instanceof Date &&
                b[unselectedSortKey.key] instanceof Date &&
                a[unselectedSortKey.key].getTime() ===
                    b[unselectedSortKey.key].getTime()) {
                return a?.value > b?.value ? directionVal : 0 - directionVal;
            }
            return a[unselectedSortKey.key] > b[unselectedSortKey.key]
                ? directionVal
                : 0 - directionVal;
        });
    };
    getUnselectedOptions = selectedOptions => this.sortItems(_.differenceBy(this.props.allOptions, selectedOptions, 'value'));
    constructor(props) {
        super(props);
        const { initialSelectedValues, allOptions } = props;
        const selectedOptions = initialSelectedValues.map(value => _.find(allOptions, { value }));
        this.state = {
            allOptions,
            selectedOptions,
            unselectedOptions: this.getUnselectedOptions(selectedOptions),
        };
    }
    refreshElement = el => {
        if (!el)
            return;
        const display = window.getComputedStyle(el)?.display;
        el.style.display = 'none';
        el.offsetHeight;
        el.style.display = display;
    };
    componentDidMount() {
        this.refreshElement(this.selectedListRef?.el);
        this.refreshElement(this.unselectedListRef?.el);
    }
    componentDidUpdate(prevProps, prevState) {
        const { allOptions, initialSelectedValues, autoScrollDown, triggerUpdate } = this.props;
        if (triggerUpdate !== prevProps.triggerUpdate) {
            const selectedOptions = initialSelectedValues.map(value => _.find(allOptions, { value }));
            const unselectedOptions = this.getUnselectedOptions(selectedOptions);
            setTimeout(() => {
                this.setState({
                    unselectedOptions,
                });
            }, 10);
        }
        if (!_.isEqual(prevProps.initialSelectedValues, initialSelectedValues)) {
            const selectedOptions = initialSelectedValues.map(value => _.find(allOptions, { value }));
            this.setState({
                selectedOptions,
                allOptions,
            }, () => {
                if (autoScrollDown && this.selectedListRef)
                    _.delay(() => {
                        this.selectedListRef.el.scrollTop =
                            this.selectedListRef.el.scrollHeight;
                    });
            });
        }
    }
    arraysMatch = (a, b) => a.length === b.length && a.join('|') === b.join('|');
    getValuesFromOptions = options => options.map(option => option.value);
    onChange = (options, isSelectedList) => {
        const { allowUpdate } = this.props;
        if (!allowUpdate || !allowUpdate(options, isSelectedList))
            return;
        if (isSelectedList) {
            const curValues = this.getValuesFromOptions(this.state.selectedOptions);
            const newValues = this.getValuesFromOptions(options);
            if (!this.arraysMatch(curValues, newValues)) {
                this.setState({
                    selectedOptions: options,
                });
                this.props.onUpdate(newValues);
            }
        }
        else {
            this.setState({
                unselectedOptions: this.sortItems(options),
            });
        }
    };
    onAdd = value => {
        const newlySelectedOptions = [
            ...this.state.selectedOptions,
            _.find(this.state.allOptions, { value }),
        ];
        this.setState({
            selectedOptions: newlySelectedOptions,
            unselectedOptions: this.getUnselectedOptions(newlySelectedOptions),
        });
        this.props.onUpdate(newlySelectedOptions.map(option => option.value));
        if (this.selectedListRef)
            _.delay(() => {
                this.selectedListRef.el.scrollTop = this.selectedListRef.el.scrollHeight;
            });
    };
    onRemove = value => {
        const removeOption = _.find(this.state.selectedOptions, { value });
        if (removeOption && removeOption.isNewUploaded) {
            removeOption.openDeleteModal(removeOption);
            return;
        }
        const newlySelectedOptions = _.filter(this.state.selectedOptions, option => option.value !== value);
        this.setState({
            selectedOptions: newlySelectedOptions,
            unselectedOptions: this.getUnselectedOptions(newlySelectedOptions),
        });
        this.props.onUpdate(newlySelectedOptions.map(option => option.value));
    };
    render() {
        const { elmId, height, selectedListHeader, selectedListStyle, selectedListItemRenderer, unselectedListHeader, unselectedListStyle, unselectedListItemRenderer, helpText, showAsGrid, hideRightPanel, } = this.props;
        return (_jsxs("div", { "data-elm-id": elmId, style: { height }, className: cx('container'), children: [_jsxs("div", { className: cx('panel-container'), children: [_jsxs("div", { className: cx('panel', 'left-side'), style: selectedListStyle, children: [_jsx(Text, { "data-elm-id": 'selected_list_header', size: 'tiny', weight: 'heavy', children: selectedListHeader }), _jsx(SortableList, { elmId: 'selected_list', captureListRef: ref => {
                                        if (ref)
                                            this.selectedListRef = ref.sortable;
                                    }, list: this.state.selectedOptions || [], canSort: true, onChange: values => {
                                        this.onChange(values, true);
                                    }, optionRenderer: selectedListItemRenderer.bind(null, this.onRemove), showAsGrid: showAsGrid })] }), !hideRightPanel && (_jsxs("div", { className: cx('panel', 'right-side', { 'grid-mode': showAsGrid }), style: unselectedListStyle, children: [_jsx(Text, { "data-elm-id": 'unselected_list_header', size: 'tiny', weight: 'heavy', children: unselectedListHeader }), _jsx(SortableList, { elmId: 'unselected_list', captureListRef: ref => {
                                        if (ref)
                                            this.unselectedListRef = ref.sortable;
                                    }, list: this.state.unselectedOptions || [], canSort: true, onChange: values => {
                                        this.onChange(values, false);
                                    }, optionRenderer: unselectedListItemRenderer.bind(null, this.onAdd), showAsGrid: showAsGrid })] }))] }), helpText && (_jsx("p", { "data-elm-id": 'help_text', className: cx('help-text'), children: helpText }))] }));
    }
}
export default Accumulator;
//# sourceMappingURL=index.js.map